import { stringifyUrl } from "query-string";
import { UsersLoader } from "./UsersLoader";
import { UserResponse, User, DeleteUserInfo, UpdateUserInfo, CreateUserInfo } from "../typings/Users";
import { IApiResourceResponse } from "../../@typings/client";
import { FiltersQuerySerialized } from "../../helpers/serializers/filters";
import { Nullable } from "../../@typings/typings";
import { UserRoleEnum } from "../../static/enums/UserRole";
import { Page } from "../../mobx/stores/TableStore/TableStore";

export interface PageQuery {
  "page[size]": string;
  "page[number]": string;
}

export type UsersQuery = {
  include?: UsersIncludes[];
  with_trashed?: "0" | "1";
  "filter[role]"?: UserRoleEnum;
} & Partial<PageQuery> &
  Partial<FiltersQuerySerialized> &
  Record<string, Nullable<string | string[]>>;

export type UsersIncludes =
  | "profiles"
  | "profiles.admin"
  | "profiles.supervisor"
  | "profiles.tutor"
  | "profiles.parent"
  | "profiles.student"
  | "profiles.assistant"
  | "profiles.contenter"
  | "profiles.related_as_primary"
  | "profiles.related_as_primary.admin"
  | "profiles.related_as_primary.supervisor"
  | "profiles.related_as_primary.tutor"
  | "profiles.related_as_primary.student"
  | "profiles.related_as_primary.parent"
  | "profiles.related_as_primary.assistant"
  | "profiles.related_as_primary.contenter"
  | "profiles.related_as_secondary"
  | "profiles.related_as_secondary.admin"
  | "profiles.related_as_secondary.supervisor"
  | "profiles.related_as_secondary.tutor"
  | "profiles.related_as_secondary.student"
  | "profiles.related_as_secondary.parent"
  | "profiles.related_as_secondary.assistant"
  | "profiles.related_as_secondary.contenter"
  | "profiles.related_as_primary.user"
  | "profiles.related_as_secondary.user"
  | "permissions";

export class UsersApi {
  public static me(query: UsersQuery = {}) {
    const url = stringifyUrl({ url: "/users/me", query }, { arrayFormat: "comma" });
    return UsersLoader.get<UserResponse>({ url });
  }

  public static users(query: UsersQuery = {}) {
    const url = stringifyUrl({ url: "/users", query }, { arrayFormat: "comma" });
    return UsersLoader.get<IApiResourceResponse<User, { page: Page }, { autologin_url: string }>>({ url });
  }

  public static createUser(data: IApiResourceResponse<CreateUserInfo>, query: UsersQuery = {}) {
    const url = stringifyUrl({ url: "/users", query }, { arrayFormat: "comma" });
    return UsersLoader.post<IApiResourceResponse<User, {}, { autologin_url: string; password_plain: string }>>({
      url,
      data,
    });
  }

  public static user(id: string, query: UsersQuery = {}) {
    const url = stringifyUrl({ url: `/users/${id}`, query }, { arrayFormat: "comma" });
    return UsersLoader.get<UserResponse>({ url });
  }

  public static restoreUser(id: string, data: IApiResourceResponse<DeleteUserInfo<null>>, query: UsersQuery = {}) {
    const url = stringifyUrl({ url: `/users/${id}`, query }, { arrayFormat: "comma" });
    return UsersLoader.patch<UserResponse>({ url, data });
  }

  public static updateUser(id: string, data: IApiResourceResponse<UpdateUserInfo>, query: UsersQuery = {}) {
    const url = stringifyUrl({ url: `/users/${id}`, query }, { arrayFormat: "comma" });
    return UsersLoader.patch<UserResponse>({ url, data });
  }

  public static deleteUser(id: string, data: IApiResourceResponse<DeleteUserInfo>, query: UsersQuery = {}) {
    const url = stringifyUrl({ url: `/users/${id}`, query }, { arrayFormat: "comma" });
    return UsersLoader.patch<UserResponse>({ url, data });
  }
}
